/**
 * @copyright
 * Copyright (c) 2025 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import { RequestStatus } from "~/constants";
import {
  AchAccountType,
  ComplianceDocumentStatus,
  CompliancePayeeType,
  ComplianceReviewStatusName,
  ComplianceStatus,
  ComplianceTaxpayerType,
  DocumentType,
  PayMethod,
  TaxpayerRegion,
} from "~/features/myAccount/constants";
import { WebserviceResponse } from "~/types";

export interface MyAccountState {
  myDetails: {
    meta: {
      saveStatus: RequestStatus;
      initStatus: RequestStatus;
    };
    data: {
      email: string;
      phone: string;
    };
  };
  payoutInfo: {
    meta: {
      initStatus: RequestStatus;
      saveStatus: RequestStatus;
    };
    data: {
      payoutData: PayoutData | null;
      countries: Record<string, PayoutCountry>;
      restrictedCountries: Record<number, string[]>;
      paymethodInfo: PaymethodInfo;
      missingData: string[];
    };
  };
  documents: {
    meta: {
      initStatus: RequestStatus;
      uploadStatus: RequestStatus;
      deleteStatus: { [id: number]: RequestStatus };
    };
    data: {
      pendingDocuments: {
        byId: { [id: number]: ComplianceDocument };
        ids: number[];
      };
      complianceStatus: ComplianceStatus;
      reviewData: ReviewData | null;
      payoutInfo: {
        resellerType: string | null;
      };
      reviewDocuments: {
        byId: { [id: number]: ComplianceDocument };
        ids: number[];
      };
    };
  };
  compliance: {
    meta: {
      initStatus: RequestStatus;
      saveStatus: RequestStatus;
      uploadStatus: RequestStatus;
      deleteStatus: { [id: number]: RequestStatus };
    };
    data: {
      details: ComplianceDetails;
      review: ComplianceReviewDetails;
      countries: Record<string, PayoutCountry>;
      documents: {
        pending: ComplianceDocumentWithStatus[];
        submitted: ComplianceDocumentWithStatus[];
      };
    };
  };
}

export type PaymethodInfo = Record<number, { id: number; key: string; enabled: boolean; minPayoutDollarCents: number }>;

export interface PayoutCountry {
  countryCode: string;
  payment?: {
    requiresSWIFT: boolean;
    requiresIBAN: boolean;
    requiresBBAN: boolean;
    requiresBankCode: boolean;
  };
  states?: Record<string, string>;
}

export interface ReviewData {
  created: string;
  id: number;
  note: string;
  printedby: string;
  printedtime: string | null;
  resellerid: number;
  reviewedby: string;
  status: number;
  updated: string;
}

export interface SaveResellerInfoPayload {
  phone?: string;
  email: string;
  password: string;
  new_password?: string;
  verify_new_password?: string;
}

export interface PostSaveResellerInfoPayload {
  phone?: string;
  email: string;
  password: string;
  newPassword?: string;
  newPasswordVerify?: string;
}

export interface PayoutData {
  submitted: number | null;
  addressee: string;
  address1: string;
  address2: string;
  address3: string;
  city: string;
  state: string;
  country: string;
  phone: string;
  zip: string;
  pay_method: PayMethod;
  hold_status: number;
  minimum_amount_dollars: number;
  payoneer_payee_id: string;
  payoneer_status: number;
  wire_signup: string;
  pri_account_number: string;
  pri_bank_name: string;
  pri_accountholder: string;
  pri_address1: string;
  pri_address2: string;
  pri_address3: string;
  pri_city: string;
  pri_state: string;
  pri_zip: string;
  pri_country: string;
  pri_routing_code: string;
  pri_addl_info1: string;
  pri_addl_info2: string;
  pri_addl_info3: string;
  pri_branch_code: string;
  pri_iban_code: string;
  pri_account_type: string;
  pri_transit_number: string;
  pri_financial_institution_number: string;
  us_taxpayer: string;
  eu_taxpayer: string;
  tax_id: string;
  eu_tax_id: string;
  taxpayer_region?: TaxpayerRegion;
  pri_swift_code: string;
  paxum_email: string;
  cosmo_san: string;
  ach_signup: string;
  ach_account_number: string;
  ach_bank_name: string;
  ach_accountholder: string;
  ach_routing_code: string;
  ach_account_type?: AchAccountType;
  masspay_email: string;
  review_status: number;
  reseller_type: string;
  date_of_birth: string;
  business_registration_number: number | null;
  rocketfuel_email: string | null;
  rocketfuel_first_name: string | null;
  rocketfuel_last_name: string | null;
}

export interface PostPaymentInfoResponseData {
  code: number;
  message?: string;
}

export interface GetPaymentInfoResponseData {
  code: number;
  data: {
    submitted: number;
    addressee: string;
    address1: string;
    address2: string;
    address3: string;
    city: string;
    state: string;
    country: string;
    phone: string;
    zip: string;
    pay_method: PayMethod;
    hold_status: number;
    minimum_amount: string;
    payoneer_payee_id: string;
    payoneer_status: number;
    wire_signup: string;
    pri_account_number: string;
    pri_bank_name: string;
    pri_accountholder: string;
    pri_address1: string;
    pri_address2: string;
    pri_address3: string;
    pri_city: string;
    pri_state: string;
    pri_zip: string;
    pri_country: string;
    pri_routing_code: string;
    pri_addl_info1: string;
    pri_addl_info2: string;
    pri_addl_info3: string;
    pri_branch_code: string;
    pri_iban_code: string;
    pri_account_type: string;
    pri_transit_number: string;
    pri_financial_institution_number: string;
    us_taxpayer: string;
    eu_taxpayer: string;
    tax_id: string;
    eu_tax_id: string;
    pri_swift_code: string;
    paxum_email: string;
    cosmo_san: string;
    ach_signup: string;
    ach_account_number: string;
    ach_bank_name: string;
    ach_accountholder: string;
    ach_routing_code: string;
    ach_account_type: AchAccountType;
    masspay_email: string;
    review_status: number;
    reseller_type: string;
    date_of_birth: string;
    business_registration_number: number | null;
    rocketfuel_email: string;
    rocketfuel_first_name: string;
    rocketfuel_last_name: string;
  };
  validation: {
    country: {
      options: string[];
    };
    pri_country: {
      options: string[];
    };
    state: {
      extras: Record<string, string>;
    };
  };
  restrictedCountries: Record<number, string[]>;
  paymethodInfo: PaymethodInfo;
  missingData: string[];
}

export interface PostPaymentInfoPayload {
  address1: string;
  address2: string;
  addressee: string;
  city: string;
  country: string;
  minimum_amount: number;
  pay_method: number;
  pri_account_number: string;
  pri_accountholder: string;
  pri_address1: string;
  pri_bank_name: string;
  pri_city: string;
  pri_country: string;
  pri_routing_code: string;
  pri_state: string;
  state: string;
  tax_id: string;
  eu_tax_id: string;
  us_taxpayer: string;
  eu_taxpayer: string;
  zip: string;
  pri_zip: string;
  password: string;
  paxum_email: string;
  cosmo_san: string;
  ach_accountholder: string;
  ach_bank_name: string;
  ach_account_number: string;
  ach_routing_code: string;
  ach_account_type: string;
  masspay_email: string;
  reseller_type: string;
  business_registration_number: string;
  date_of_birth: string;
  rocketfuel_email: string;
  rocketfuel_first_name: string;
  rocketfuel_last_name: string;
}

export interface PostSaveResellerInfoResponseData {
  code: number;
}

export interface GetComplianceStatusResponseData {
  status: number | null;
}

export type GetComplianceReviewSuccessResponseData = ReviewData;

export interface GetComplianceReviewFailResponseData {
  errors: string[];
}

export interface ComplianceDocument {
  id: number;
  documentType: DocumentType;
  filename: string;
  resellerNote: string;
  expiresAt: string | null;
  createdAt: string;
}

export interface ComplianceDocumentWithStatus extends ComplianceDocument {
  status: ComplianceDocumentStatus;
}

export interface ComplianceDocumentApiSubType {
  created: string;
  deletionstatus: number;
  documenttype: string;
  expires: string | null;
  externalid: string;
  filename: string;
  id: number;
  resellerid: number;
  resellernote: string;
  updated: string;
  status: ComplianceDocumentStatus;
}

// TODO remove "WithStatus" when we remove the previous document logic
export interface ComplianceDocumentApiSubTypeWithStatus extends ComplianceDocumentApiSubType {
  status: ComplianceDocumentStatus;
}

export interface GetComplianceReviewInfoResponseData {
  ReviewData: {
    created: string;
    id: number;
    note: string;
    printedby: string;
    printedtime: string;
    resellerid: number;
    reviewedby: string;
    status: number;
    updated: string;
  };
  ReviewDocs: ComplianceDocumentApiSubType[];
}

export interface UploadComplianceDocumentPayload {
  documenttype: string;
  resellernote: string;
  uploads: File[];
}

export type PostUploadComplianceDocumentResponseData = WebserviceResponse<{
  success: boolean;
}>;

export interface PostUploadComplianceDocumentMetaPayload {
  externalid: string;
  filename: string;
  documenttype: string;
  resellernote: string;
}

export type UploadComplianceDocumentErrorResponse = WebserviceResponse<{
  maxLimitReached?: boolean;
}>;

export interface PostDeleteDocumentPayload {
  documentid: number;
}

export interface GetWorldPayFieldMappingsResponseData {
  [countryCode: string]: {
    RequiresSWIFT: boolean;
    RequiresIBAN: boolean;
    RequiresBBAN: boolean;
    RequiresBankCode: boolean;
  };
}

export type PaymentMethodFields = {
  [key in PayMethod]: string[];
};

export enum ComplianceReviewStatus {
  UNKNOWN = -1,
  PENDING = 0,
  REJECTED = 1,
  APPROVED = 2,
  DRAFT = 3,
}

export interface ComplianceDetailsFromApi {
  id: number;
  address1: string | null;
  address2: string | null;
  city: string | null;
  country: string | null;
  dateOfBirth: string | null;
  payeeName: string | null;
  payeeType: number;
  reviewStatus: ComplianceReviewStatus;
  state: string | null;
  taxId: string | null;
  taxpayerType: number;
  zip: string | null;
  created: string | null;
  updated: string | null;
  note: string | null;
  reviewedAt: string | null;
}

export interface ComplianceDetails {
  address1: string;
  address2: string;
  city: string;
  country: string;
  dateOfBirth: string;
  payeeName: string;
  payeeType: CompliancePayeeType | null;
  reviewStatus: ComplianceReviewStatus;
  state: string | null;
  taxId: string;
  taxpayerType: ComplianceTaxpayerType | null;
  zip: string;
}

interface ComplianceReviewStatusVerbose {
  name: ComplianceReviewStatusName;
  value: ComplianceReviewStatus;
}

export interface ComplianceReviewDetails {
  status: ComplianceReviewStatus;
  reviewedAt: string;
  note: string;
  reviewId: number;
}

export interface GetComplianceDetailsResponse {
  message: string;
  status: string;
  result: {
    details: Partial<ComplianceDetailsFromApi>;
    docs: ComplianceDocumentApiSubType[];
    status: ComplianceReviewStatusVerbose;
  };
}

export interface PostComplianceDetailsPayload {
  reviewId: number;
  payeeType: number;
  taxpayerType: number;
  payeeName: string;
  address1: string;
  address2: string;
  city: string;
  state?: string;
  zip: string;
  country: string;
  dateOfBirth?: string;
  taxId?: string;
}

export interface PostComplianceDetailsResponse {
  status: string;
  error?: string;
  message: string;
  result: {
    status: string;
  } | null;
}

export interface PostUpdateComplianceRequestBody {
  reviewId: number;
  payeeType?: number;
  taxpayerType?: number;
  payeeName?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  dateOfBirth?: string;
  taxId?: string;
}

export interface PostFinalizeComplianceRequestBody {
  reviewId: number;
}

export interface GetValidLocationsResponse {
  message: string;
  status: string;
  result: {
    countries: Record<string, string>;
    states: Record<string, string>;
  };
}
